<template>
  <div class="rbz-page" data-qa="page" ref="rbzPageRef">
    <div class="rbz-page-header" data-qa="page-header" ref="rbzPageHeaderRef">
      <div class="rbz-page-header-first-level">
        <div class="left rbz-text-h3">
          <slot name="pageHeadline" />
        </div>
        <div class="right">
          <slot name="pageHeadlineRight" />
        </div>
      </div>
      <layout-divider-line color="gray" class="rbz-page-header-divider" />
      <div
        class="rbz-page-header-second-level"
        v-if="
          $slots.secondLevelHeaderEntire ||
          $slots.secondLevelHeaderLeft ||
          $slots.secondLevelHeaderRight
        "
      >
        <div class="entire">
          <slot name="secondLevelHeaderEntire" />
        </div>
        <div class="left">
          <slot name="secondLevelHeaderLeft" />
        </div>
        <div class="right">
          <slot name="secondLevelHeaderRight" />
        </div>
      </div>
    </div>
    <div class="rbz-page-content" data-qa="page-content" ref="rbzPageContentRef">
      <rbz-loader
        v-if="showLoader"
        :loading="!!isLoading"
        :data-exists="!!dataExists"
        height="100%"
        :style="{ 'align-items': 'start', 'padding-top': '150px' }"
      />
      <template v-if="!showLoader || dataExists">
        <div v-show="!isLoading">
          <slot
            name="pageContent"
            :rbz-page-ref="$refs.rbzPageRef"
            :rbz-page-header-ref="$refs.rbzPageHeaderRef"
            :rbz-page-content-ref="$refs.rbzPageContentRef"
            :scroll-to-element="scrollToElement"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzLoader from '@/components/RbzLoader.vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

interface PageTemplate {
  showLoader?: boolean;
  isLoading?: boolean;
  dataExists?: boolean;
}

withDefaults(defineProps<PageTemplate>(), {
  showLoader: true,
});

const rbzPageRef = ref(null);
const rbzPageHeaderRef = ref(null);
const rbzPageContentRef = ref(null);

const fixFocus = (event: FocusEvent) => {
  if (
    !rbzPageContentRef.value.contains(event.target) ||
    event.target.classList.contains('ignore-fix-focus')
  ) {
    return;
  }
  const headerBottomPosition = rbzPageHeaderRef.value.getBoundingClientRect().bottom;
  const focusedElement = event.target.getBoundingClientRect();
  const focusedElementTop = focusedElement.top - 20;
  if (focusedElementTop < headerBottomPosition) {
    nextTick(() => {
      rbzPageRef.value.scrollBy(0, focusedElementTop - headerBottomPosition);
    });
  }
};

const scrollToElement = (element: HTMLElement, offsetTop?: number) => {
  if (!rbzPageContentRef.value.contains(element)) {
    return;
  }
  // out of screen top
  const headerBottomPosition = rbzPageHeaderRef.value.getBoundingClientRect().bottom;
  const elementTop = element.getBoundingClientRect().top - offsetTop;
  if (elementTop < headerBottomPosition) {
    nextTick(() => {
      rbzPageRef.value.scrollTo(0, element.offsetTop - element.clientHeight);
    });
  }
  // out of screen bottom
  if (element.getBoundingClientRect().bottom > window.innerHeight) {
    nextTick(() => {
      const distanceFromEdge = element.getBoundingClientRect().top - window.innerHeight;
      rbzPageRef.value.scrollBy(0, element.clientHeight + distanceFromEdge);
    });
  }
};

onMounted(() => {
  document.addEventListener('focusin', fixFocus);
});

onBeforeUnmount(() => {
  document.removeEventListener('focusin', fixFocus);
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'ui-component-library/assets/styles/main';
@use 'sass:map';

.rbz-page-header {
  background-color: map.get(colors.$colors, 'white');
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
}

.rbz-page-header-first-level {
  height: 30px;
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.rbz-page-header-second-level {
  .entire {
    width: 100%;
  }

  .left {
    align-items: center;
    display: flex;
    float: left;
    gap: 12px;
    position: relative;

    :deep(.vertical-divider) {
      height: 24px;
      position: relative;
    }
  }

  .right {
    display: flex;
    float: right;
    gap: 12px;
    position: relative;
  }
}

:deep(.switch-page-entry) {
  max-width: 25vw;
  width: fit-content;

  .dropdown-header {
    border-color: transparent;
    max-height: 36px;
    min-height: 36px;
    padding: 5px 11px;

    &.open {
      background-color: map.get(colors.$colors, 'white-ice');
      border-radius: 4px;
      box-shadow: none;
    }

    &:active {
      background-color: map.get(colors.$colors, 'white-ice');
      border-color: map.get(colors.$colors, 'jewel');
    }

    &:focus:not(:active) {
      background-color: map.get(colors.$colors, 'white');
      border-color: map.get(colors.$colors, 'jewel');
      box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
      outline: none;
    }
  }

  .dropdown-placeholder {
    @extend .rbz-text-h4;

    color: map.get(colors.$colors, 'stratos');
    margin-bottom: 0;
  }

  .dropdown-list {
    border-top-width: 1px;
    margin-top: 4px;
    max-width: 50vw;
    min-width: 100%;
    width: fit-content;
  }

  .menu-list-item {
    height: 100%;
  }

  .item-title {
    white-space: normal;
    word-wrap: break-word;
  }
}

.rbz-page-content {
  background-color: map.get(colors.$colors, 'white');
  padding: 0 32px 32px;
  position: relative;
  overflow: hidden auto;
  height: 100%;
}

.rbz-page-content > div {
  background-color: inherit;
}

.rbz-page {
  display: flex;
  flex-direction: column;
}
</style>
